import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {Transition} from 'react-transition-group';

import Avatar from '../Avatar';
import FlatButton from '../FlatButton';
import GearsIcon from '../Icons/SingleGearIcon';
import CloseIcon from '../Icons/CloseIcon';

import cl from 'classnames';
import s from './AccountWidget.css';
import {Trans} from '@lingui/react';

const defaultOpen = false;
const defaultAvatarSize = 40;

/**
 * Account widget
 */
export default class AccountWidget extends PureComponent {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.menuRef = React.createRef();

    this.state = {
      open: false,
    };
  }

  toggleMenu = ev => {
    if (!this.props.showCloseButton) {
      this.setState(prevState => ({open: !prevState.open}));
    }

    if (this.props.onClick && ev) {
      this.props.onClick(ev);
      return;
    }
  };

  handleFocusIn = event => {
    // Se il focus entra in uno dei bottoni del menu, apri il menu
    if (this.menuRef.current && this.menuRef.current.contains(event.target)) {
      if (!this.state.open) {
        this.setState({open: true});
      }
    }
  };

  handleFocusOut = event => {
    // Quando il focus esce dal menu, chiudi il menu
    if (this.menuRef.current && !this.menuRef.current.contains(event.relatedTarget)) {
      if (this.state.open) {
        this.setState({open: false});
      }
    }
  };

  componentDidMount() {
    if (this.containerRef.current) {
      this.containerRef.current.addEventListener('focusin', this.handleFocusIn);
      this.containerRef.current.addEventListener('focusout', this.handleFocusOut);
    }
  }

  componentWillUnmount() {
    if (this.containerRef.current) {
      this.containerRef.current.removeEventListener('focusin', this.handleFocusIn);
      this.containerRef.current.removeEventListener('focusout', this.handleFocusOut);
    }
  }

  render() {
    const {
      name,
      children,
      avatarSize,
      onClick,
      clickable,
      className,
      style,
      showOpenButton,
      showCloseButton,
      onClose,
    } = this.props;
    const {open} = this.state;
    return (
      <div ref={this.containerRef} className={s.Container}>
        <div
          className={cl(s.AccountWidget, className)}
          style={style}
          onClick={this.toggleMenu}
          role="button"
          tabIndex={-1}
          aria-label={name}
        >
          <Avatar
            letterClassName={s.CustomLetter}
            size={avatarSize}
            letter={name}
            className={s.Avatar}
          />

          <section className={cl(s.NameContainer, 'EA-AW__NameContainer')}>
            <span className={s.Name}>{name}</span>
            {showOpenButton ? (
              <div className={cl(s.Options, open && s.isOpen)}>
                <GearsIcon
                  className={cl(s.Icon, open && s.isOpen)}
                  customColor={open && '#ffffff'}
                />
              </div>
            ) : null}
            {showCloseButton ? (
              <FlatButton
                onClick={onClose}
                className={cl(s.CloseButton, s.NotUnderlined)}
                aria-label="Chiudi test"
              >
                <CloseIcon />
              </FlatButton>
            ) : null}
          </section>
        </div>
        <Transition timeout={1} in={open}>
          {status => {
            // I bottoni sono sempre accessibili per screen reader (no tabIndex: -1, no aria-hidden)
            // ma visivamente si mostrano/nascondono con le classi CSS
            return (
              <nav
                ref={this.menuRef}
                id="account-menu"
                className={cl(s.Menu, status && s[`Menu-${status}`])}
                role="menu"
                aria-label="Menu impostazioni account"
              >
                {children}
              </nav>
            );
          }}
        </Transition>
      </div>
    );
  }
}
AccountWidget.propTypes = {
  /**
   * The user name
   */
  name: PropTypes.string,
  /**
   * Whether the menu is open
   */
  open: PropTypes.bool,
  /**
   * The avatar component size
   */
  avatarSize: PropTypes.number,
  /**
   * Click event handler
   */
  onClick: PropTypes.func,
  /**
   * Container class name
   */
  className: PropTypes.string,
  /**
   * Container styles
   */
  style: PropTypes.object,
};
AccountWidget.defaultProps = {
  open: defaultOpen,
  avatarSize: defaultAvatarSize,
};
